import type { NavigateFunction } from 'react-router-dom';

/**
 * Диплинк, который бэк присылает у сторис и баннеров в поле `target`.
 *
 * Форматы: "product/1073" — карточка блюда, "category/85" — скролл к категории
 * на главной, "https://…" — внешняя ссылка, всё остальное считаем готовым
 * роутом.
 */
export type Deeplink =
  | { kind: 'product'; productId: number }
  | { kind: 'category'; categoryId: number }
  | { kind: 'external'; url: string }
  | { kind: 'route'; path: string };

const getIdFromTarget = (target: string) => Number(target.split('/')[1]);

/**
 * Разбирает `target` в диплинк. Возвращает null, если вести некуда: пустая
 * строка или "product/" без id — это не переход, а неверно заполненный баннер.
 */
export const parseDeeplink = (target?: string | null): Deeplink | null => {
  const trimmed = target?.trim();

  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith('product/')) {
    const productId = getIdFromTarget(trimmed);

    return productId > 0 ? { kind: 'product', productId } : null;
  }

  if (trimmed.startsWith('category/')) {
    const categoryId = getIdFromTarget(trimmed);

    return categoryId > 0 ? { kind: 'category', categoryId } : null;
  }

  if (trimmed.startsWith('http')) {
    return { kind: 'external', url: trimmed };
  }

  return { kind: 'route', path: trimmed };
};

/**
 * Выполняет переход по уже разобранному диплинку.
 *
 * Категория не имеет своего экрана: главная читает `scrollToCategory` из
 * sessionStorage и доезжает до нужного блока сама (см. Home.tsx). Поэтому
 * сначала кладём id, потом уводим на "/".
 */
export const followDeeplink = (link: Deeplink, navigate: NavigateFunction) => {
  switch (link.kind) {
    case 'product':
      navigate(`/${link.productId}`);
      return;
    case 'category':
      sessionStorage.setItem('scrollToCategory', String(link.categoryId));
      navigate('/');
      return;
    case 'external':
      window.location.href = link.url;
      return;
    case 'route':
      navigate(link.path);
  }
};

export const navigateToTarget = (target: string, navigate: NavigateFunction) => {
  const link = parseDeeplink(target);

  if (!link) {
    return;
  }

  followDeeplink(link, navigate);
};
